import { useCallback, useMemo } from "react"
import { useRouter } from "next/router"

import { useAuth, useOrganizationList } from "@clerk/nextjs"

import { useTransportWorkspaceScope } from "../contexts/TransportWorkspaceScopeContext"
import { getTransportTokenForOrganization } from "../utils/transportAuthToken"
import { createTransportClient } from "../utils/transportClient"
import {
  getRouteOrganizationSlug,
  isTransportOrganizationSynced,
  isTransportRouteOrganizationActive,
} from "../utils/transportOrganizationContext"
import type { TransportClientOptions } from "./transportQueryTypes"

export function useTransportClient({
  organizationSlugOverride = null,
}: TransportClientOptions = {}) {
  const {
    getToken,
    isLoaded: isAuthLoaded,
    orgId: clerkOrganizationId,
    orgSlug: organizationSlug,
  } = useAuth()
  const workspaceId = useTransportWorkspaceScope()
  const getWorkspaceId = useCallback(() => workspaceId, [workspaceId])
  const { isLoaded: membershipsLoaded, userMemberships } = useOrganizationList({
    userMemberships: { infinite: true },
  })
  const router = useRouter()
  const routeOrganizationSlug = useMemo(
    () => organizationSlugOverride ?? getRouteOrganizationSlug(router.asPath),
    [organizationSlugOverride, router.asPath],
  )
  const routeOrganizationMembership = useMemo(
    () =>
      routeOrganizationSlug
        ? userMemberships?.data?.find(
            (membership) =>
              membership.organization.slug === routeOrganizationSlug,
          )
        : undefined,
    [routeOrganizationSlug, userMemberships?.data],
  )
  const activeOrganizationIsRoute =
    routeOrganizationSlug !== null &&
    organizationSlug === routeOrganizationSlug &&
    clerkOrganizationId != null
  const routeOrganizationId =
    routeOrganizationMembership?.organization.id ??
    (activeOrganizationIsRoute ? clerkOrganizationId : null)
  const transportOrganizationSlug = routeOrganizationSlug ?? organizationSlug
  const transportOrganizationId = routeOrganizationSlug
    ? routeOrganizationId
    : clerkOrganizationId
  const activeOrganizationMatchesRoute = isTransportRouteOrganizationActive({
    activeOrganizationId: clerkOrganizationId ?? null,
    activeOrganizationSlug: organizationSlug ?? null,
    routeOrganizationId,
    routeOrganizationSlug,
  })
  const isRouteOrganizationResolved =
    routeOrganizationSlug === null ||
    activeOrganizationIsRoute ||
    (membershipsLoaded && transportOrganizationId != null)
  const isOrganizationSynced =
    routeOrganizationSlug === null
      ? isTransportOrganizationSynced({
          activeOrganizationSlug: organizationSlug ?? null,
          routeOrganizationSlug,
        })
      : isRouteOrganizationResolved && activeOrganizationMatchesRoute
  // Activation belongs to RouteOrganizationGate, which resolves the route
  // organization before organization-scoped routes mount. Transport reads that
  // organization and mints for it; a second writer here would race the gate.
  const getFreshToken = useCallback(
    () =>
      getTransportTokenForOrganization({
        getToken,
        organizationId: transportOrganizationId ?? undefined,
        organizationSlug: transportOrganizationSlug ?? undefined,
      }),
    [getToken, transportOrganizationId, transportOrganizationSlug],
  )
  const getOrganizationContext = useCallback(
    () =>
      transportOrganizationSlug ? { slug: transportOrganizationSlug } : null,
    [transportOrganizationSlug],
  )

  const getClient = useCallback(
    () =>
      createTransportClient({
        getOrganizationContext,
        getToken: getFreshToken,
        getWorkspaceId,
      }),
    [getFreshToken, getOrganizationContext, getWorkspaceId],
  )

  return {
    getClient,
    isAuthLoaded,
    isOrganizationSynced,
    organizationSlug: transportOrganizationSlug ?? null,
    workspaceId,
  }
}

export function useTransportOrganizationContext() {
  const { isAuthLoaded, isOrganizationSynced, organizationSlug } =
    useTransportClient()

  return {
    isAuthLoaded,
    isOrganizationSynced,
    organizationSlug,
  }
}
